import { invalidateAbsences } from '@/api/absence';
/**
 * Approve several shifts, shared by Timeliste and Timer til lønn step 2 so both approve the same
 * way. One shift is PUT /admin/shifts/:id/accept; several are one POST /admin/shifts/approve-batch
 * (#295 ONB-15): every record at the revision shown, all or none, so a record changed since the
 * confirmation refuses the batch and nothing is approved. Then one refetch of the shifts and the
 * pay report, which is read from approved shifts only.
 *
 * #27 (D19 A, owner 2026-10-02: warn, never block): a shift on a day the person has an approved
 * paid whole-day absence is asked about first, naming person and date. A vacation day can be given
 * back on the spot (POST /admin/absence-requests/:id/cancel-day); the manager decides, nothing
 * changes by itself. Ferieloven §5: a day worked is not feriefritid, so the day is still owed.
 */
import { post, put } from '@/api/client';
import { type ApiAbsence, type ApiShift, useAbsences } from '@/api/data';
import { invalidateShifts, revisionOf } from '@/api/shifts';
import { T } from '@/i18n';
import { oslo } from '@/rules';
import { Button, toast } from '@/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

export interface Clash {
  shift: ApiShift;
  absence: ApiAbsence;
  day: string;
}

/**
 * The shifts that fall on a day of the same person's approved whole-day absence that is paid (the
 * pay report's markWorkedDates, admin/shiftController.js) or drawn from the vacation balance: ferie
 * is is_paid false in the catalogue, paid as feriepenger, and is the case #27 is about. Not a time window.
 */
export function clashesOf(shifts: ApiShift[], absences: ApiAbsence[]): Clash[] {
  const out: Clash[] = [];
  for (const shift of shifts) {
    const day = oslo(shift.punch_in_time ?? shift.start_time)?.date ?? '';
    const absence = absences.find(
      (a) =>
        a.user_id === shift.user_id &&
        a.status === 'accepted' &&
        !!(a.absence_type?.is_paid || a.absence_type?.is_deductible) &&
        !['time', 'datetime'].includes(a.absence_type?.type ?? '') &&
        (oslo(a.start_date)?.date ?? '') <= day &&
        day <= (oslo(a.end_date)?.date ?? ''),
    );
    if (absence) out.push({ shift, absence, day });
  }
  return out;
}

export function useApproveMany(shifts: ApiShift[] = []) {
  const qc = useQueryClient();
  // nothing to warn about until the list has loaded, or for a reader without fravaer.se
  const absQ = useAbsences().data;
  const absences = Array.isArray(absQ) ? absQ : [];
  const [busy, setBusy] = useState(false);
  /** the shifts waiting for an answer about the absence days they fall on */
  const [asking, setAsking] = useState<{
    ids: number[];
    clashes: Clash[];
    reason?: string | undefined;
    seen?: ApiShift[] | undefined;
  } | null>(null);
  /** absence days given back while the question is open, by `${absenceId}:${day}` */
  const [given, setGiven] = useState<Set<string>>(new Set());

  /**
   * true when every shift was approved; `reason` goes to the history (Kontroll's «Bekreft som riktig», #275);
   * `seen` is what the confirmation showed, else the page's list
   */
  async function run(ids: number[], reason?: string, seen: ApiShift[] = shifts): Promise<boolean> {
    setBusy(true);
    let failure: string | null = null;
    // bound to the values this page showed: a row moved since is refused, nothing approved (#149, ONB-15)
    const shown = ids.map((id) => seen.find((s) => s.id === id));
    try {
      if (ids.length === 1) {
        await put(`/admin/shifts/${ids[0]}/accept`, {
          is_approved: true,
          ...(shown[0] ? { if_unchanged: revisionOf(shown[0]) } : {}),
          ...(reason ? { reason } : {}),
        });
      } else {
        if (shown.some((s) => !s)) throw new Error(T('Utvalget er endret. Se over registreringene på nytt.'));
        await post('/admin/shifts/approve-batch', {
          records: (shown as ApiShift[]).map((s) => ({ id: s.id, if_unchanged: revisionOf(s) })),
          ...(reason ? { reason } : {}),
        });
      }
    } catch (e) {
      failure = (e as Error).message;
    }
    await invalidateShifts(qc);
    setBusy(false);
    toast(
      failure
        ? ids.length > 1
          ? T('Ingenting er godkjent. {message}', { message: failure })
          : T('Godkjente {done} av {all}. Stoppet: {message}', { done: 0, all: 1, message: failure })
        : ids.length === 1
          ? T('Godkjente 1 vakt.')
          : T('Godkjente {count} vakter.', { count: ids.length }),
    );
    return failure == null;
  }

  /** true when approved now; false when refused, or when the absence question is asked first */
  async function approve(ids: number[], reason?: string, seen?: ApiShift[]): Promise<boolean> {
    if (!ids.length) return false;
    const picked = (seen ?? shifts).filter((s) => ids.includes(s.id));
    const clashes = clashesOf(picked, absences);
    if (clashes.length) {
      setGiven(new Set());
      setAsking({ ids, clashes, reason, seen });
      return false;
    }
    return run(ids, reason, seen);
  }

  async function giveBack(c: Clash): Promise<void> {
    try {
      await post(`/admin/absence-requests/${c.absence.id}/cancel-day`, {
        date: c.day,
        change_reason: T('Jobbet {day}', { day: c.day }),
      });
      setGiven((g) => new Set(g).add(`${c.absence.id}:${c.day}`));
      await invalidateAbsences(qc);
      toast(T('Feriedagen er avlyst og lagt tilbake på saldoen.'));
    } catch (e) {
      toast((e as Error).message);
    }
  }

  const name = (s: ApiShift) => (s.user ? `${s.user.name} ${s.user.lastname ?? ''}`.trim() : '');
  const dayText = (d: string) => `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(0, 4)}`;
  const dialog = asking && (
    <div className="confirm">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="absence-work-title"
        data-testid="absence-work"
        onKeyDown={(e) => e.key === 'Escape' && setAsking(null)}
      >
        <h3 id="absence-work-title">{T('Vakt på en dag med godkjent fravær')}</h3>
        <p>
          {T(
            'Dagen betales både som fravær og som arbeid hvis du godkjenner. For ferie kan du avlyse ferien denne dagen, så går dagen tilbake på saldoen.',
          )}
        </p>
        <ul className="clashes">
          {asking.clashes.map((c) => {
            const done = given.has(`${c.absence.id}:${c.day}`);
            return (
              <li key={`${c.shift.id}`}>
                <span>
                  <b>{name(c.shift)}</b>, {dayText(c.day)}: {c.absence.absence_type?.name}
                </span>
                {c.absence.absence_type?.is_deductible &&
                  (done ? (
                    <small>{T('Ferien er avlyst denne dagen')}</small>
                  ) : (
                    <Button small onClick={() => giveBack(c)}>
                      {T('Avlys ferie denne dagen')}
                    </Button>
                  ))}
              </li>
            );
          })}
        </ul>
        <div className="df">
          <Button onClick={() => setAsking(null)}>{T('Avbryt')}</Button>
          <Button
            tone="pri"
            autoFocus
            disabled={busy}
            onClick={async () => {
              const { ids, reason, seen } = asking;
              setAsking(null);
              await run(ids, reason, seen);
            }}
          >
            {T('Godkjenn likevel')}
          </Button>
        </div>
      </div>
    </div>
  );
  return { approve, busy, dialog };
}
